import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {Controller, useForm, useWatch} from "react-hook-form";
import {useSelector} from "react-redux";
import toast from "react-hot-toast";

import {classNames} from "../../../utils/Utils";
import InputSelect from "../../../components/common/form/input-select";
import InputDate from "../../../components/common/form/input-date";
import InputToggle from "../../../components/common/form/input-toggle";
import FormPopup from "../../../components/common/popup/form-popup";
import AddressAutocomplete from "../../../components/common/form/addressAutocomplete";
import {getElement, postElement} from "../../../api/config";
import {
    ADDRESS,
    ADDRESSES,
    COMPANY,
    CONNECTION,
} from "../../../api/endpoints";
import {getPathParam} from "../../../utils/converter";
import {
    AUTO_SAVE_DELAY,
    HEADER_COMPACT_CLASS,
    buildDeliveryFormDefaults,
    normalizeAddressOption,
    sanitizeFormValues,
} from "./document-header-utils";
import {hasPermissionFor} from "../../../components/partials/restricted/base-permission-wrapper";
import {permissionGroup, permissionType} from "../../../constants/permissions";

const DeliveryForm = ({document, onSubmit, isSubmitting, companyId, connectionId, isEditable}) => {
    const {t} = useTranslation();
    const {permissions} = useSelector((state) => state.userCompany);
    const canEditCustomerAddresses = hasPermissionFor(
        permissions,
        permissionGroup.CUSTOMER,
        permissionType.EDIT
    );
    const isFormEditable = Boolean(isEditable && typeof onSubmit === "function");
    const canManageAddresses = Boolean(isFormEditable && canEditCustomerAddresses);

    const formDefaults = useMemo(() => buildDeliveryFormDefaults(document), [document]);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        getValues,
        formState: {isDirty},
    } = useForm({
        defaultValues: formDefaults,
    });
    const autoSaveTimer = useRef(null);
    const [availableAddresses, setAvailableAddresses] = useState([]);
    const [areAddressesLoading, setAddressesLoading] = useState(false);
    const [addressFormTarget, setAddressFormTarget] = useState(null);
    const [addressFormError, setAddressFormError] = useState(null);
    const [isAddressSaving, setAddressSaving] = useState(false);
    const [isDeliveryDateConfirmationOpen, setDeliveryDateConfirmationOpen] = useState(false);
    const [isAwaitingDeliveryDateConfirmation, setIsAwaitingDeliveryDateConfirmation] = useState(false);
    const previousDeliveryDateRef = useRef(null);
    const watchedValues = useWatch({
        control,
        name: [
            "delivery_date",
            "connection_note",
            "destination_address",
            "delivery_address",
            "change_body_delivery_date",
        ],
    });
    const hasConnectionContext = Boolean(companyId) && Boolean(connectionId);
    const canLoadAddresses = Boolean(canEditCustomerAddresses && hasConnectionContext);

    useEffect(() => {
        reset(formDefaults);
    }, [formDefaults, reset]);

    useEffect(() => {
        previousDeliveryDateRef.current = null;
        setDeliveryDateConfirmationOpen(false);
        setIsAwaitingDeliveryDateConfirmation(false);
    }, [formDefaults.delivery_date]);

    const defaultAddressOptions = useMemo(() => {
        const options = [];
        if (formDefaults.destination_address?.id) {
            options.push(formDefaults.destination_address);
        }
        if (formDefaults.delivery_address?.id) {
            const exists = options.some((item) => item.id === formDefaults.delivery_address.id);
            if (!exists) {
                options.push(formDefaults.delivery_address);
            }
        }
        return options;
    }, [formDefaults.destination_address, formDefaults.delivery_address]);

    useEffect(() => {
        if (!canLoadAddresses) {
            setAvailableAddresses([]);
            setAddressesLoading(false);
            return;
        }

        let isActive = true;
        setAddressesLoading(true);
        const endpoint = getPathParam([COMPANY, String(companyId), CONNECTION, String(connectionId), ADDRESSES]);

        getElement(endpoint)
            .then((response) => {
                if (!isActive) {
                    return;
                }
                const addresses = Array.isArray(response?.addresses) ? response.addresses : [];
                setAvailableAddresses(addresses);
            })
            .catch((error) => {
                if (isActive) {
                    toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
                    setAvailableAddresses([]);
                }
            })
            .finally(() => {
                if (isActive) {
                    setAddressesLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [canLoadAddresses, companyId, connectionId, t]);

    const addressOptions = useMemo(() => {
        const map = new Map();
        (availableAddresses ?? [])
            .map((address) => normalizeAddressOption(address))
            .filter(Boolean)
            .forEach((option) => {
                map.set(option.id, option);
            });

        defaultAddressOptions
            .filter(Boolean)
            .forEach((option) => {
                if (option?.id) {
                    map.set(option.id, option);
                }
            });

        return Array.from(map.values());
    }, [availableAddresses, defaultAddressOptions]);

    const handleDeliveryDateFieldChange = useCallback((nextValue, rhfOnChange) => {
        const resolvedValue = nextValue || null;
        const currentValue = getValues("delivery_date") ?? null;

        if (currentValue === resolvedValue) {
            rhfOnChange(resolvedValue);
            return;
        }

        previousDeliveryDateRef.current = currentValue;
        setValue("change_body_delivery_date", false, {shouldDirty: false, shouldTouch: false});
        setIsAwaitingDeliveryDateConfirmation(true);
        setDeliveryDateConfirmationOpen(true);
        rhfOnChange(resolvedValue);
    }, [getValues, setValue]);

    const handleCancelDeliveryDateChange = useCallback(() => {
        const defaultValue = formDefaults.delivery_date ?? null;
        const previousValue = previousDeliveryDateRef.current ?? defaultValue;
        const normalizedPrevious = previousValue ?? null;
        const shouldMarkDirty = normalizedPrevious !== defaultValue;

        setDeliveryDateConfirmationOpen(false);
        setIsAwaitingDeliveryDateConfirmation(false);
        setValue("delivery_date", normalizedPrevious, {
            shouldDirty: shouldMarkDirty,
            shouldTouch: false,
        });
        setValue("change_body_delivery_date", false, {shouldDirty: false, shouldTouch: false});
        previousDeliveryDateRef.current = null;
    }, [formDefaults.delivery_date, setValue]);

    const handleConfirmDeliveryDateChange = useCallback(() => {
        setDeliveryDateConfirmationOpen(false);
        setIsAwaitingDeliveryDateConfirmation(false);
        previousDeliveryDateRef.current = null;
    }, []);

    const submit = useCallback((values) => {
        if (!isFormEditable || typeof onSubmit !== "function") {
            return;
        }

        const payload = {
            delivery_date: values.delivery_date ?? null,
            connection_note: values.connection_note ?? "",
            change_body_delivery_date: Boolean(values.change_body_delivery_date),
        };

        const mergePayload = {
            delivery_date: payload.delivery_date,
            connection_note: payload.connection_note,
        };

        if (values.destination_address?.id) {
            const destinationData = values.destination_address.data ?? {
                id: values.destination_address.id,
                label: values.destination_address.label,
            };

            payload.connection_address = {id: destinationData.id};
            mergePayload.connection_address = destinationData;
        } else {
            payload.connection_address = null;
            mergePayload.connection_address = null;
        }

        if (values.delivery_address?.id) {
            const deliveryData = values.delivery_address.data ?? {
                id: values.delivery_address.id,
                label: values.delivery_address.label,
            };

            payload.delivery_address = {id: deliveryData.id};
            mergePayload.delivery_address = deliveryData;
        } else {
            payload.delivery_address = null;
            mergePayload.delivery_address = null;
        }

        const sanitizedPayload = sanitizeFormValues(payload);

        onSubmit({
            payload: sanitizedPayload,
            merge: mergePayload,
        });
    }, [isFormEditable, onSubmit]);

    useEffect(() => {
        if (!isFormEditable || !isDirty || isSubmitting || isAwaitingDeliveryDateConfirmation) {
            if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current);
                autoSaveTimer.current = null;
            }
            return undefined;
        }

        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current);
        }

        autoSaveTimer.current = setTimeout(() => {
            handleSubmit(submit)();
            autoSaveTimer.current = null;
        }, AUTO_SAVE_DELAY);

        return () => {
            if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current);
                autoSaveTimer.current = null;
            }
        };
    }, [watchedValues, isDirty, isSubmitting, handleSubmit, submit, isFormEditable, isAwaitingDeliveryDateConfirmation]);

    const handleOpenAddressForm = useCallback((target) => {
        if (!canManageAddresses) {
            return;
        }
        setAddressFormTarget(target);
        setAddressFormError(null);
    }, [canManageAddresses]);

    const handleCloseAddressForm = useCallback(() => {
        setAddressFormTarget(null);
        setAddressFormError(null);
    }, []);

    const handleCreateAddress = useCallback(async (data) => {
        if (!canManageAddresses) {
            setAddressFormError(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!hasConnectionContext) {
            setAddressFormError(t("document.form.address_missing_context", {defaultValue: "Seleziona prima un cliente"}));
            return;
        }

        setAddressSaving(true);
        setAddressFormError(null);
        try {
            const endpoint = getPathParam([COMPANY, String(companyId), CONNECTION, String(connectionId), ADDRESS]);
            const response = await postElement(endpoint, data);
            const createdAddressResponse = response?.addresses ?? response;
            const createdAddress = Array.isArray(createdAddressResponse)
                ? createdAddressResponse[0]
                : createdAddressResponse;

            if (!createdAddress?.id) {
                throw new Error(t("document.form.address_create_error", {defaultValue: "Impossibile creare l'indirizzo"}));
            }

            setAvailableAddresses((previous) => {
                const map = new Map((previous ?? []).map((item) => [item.id, item]));
                map.set(createdAddress.id, createdAddress);
                return Array.from(map.values());
            });

            const normalized = normalizeAddressOption(createdAddress);
            if (normalized && addressFormTarget) {
                setValue(addressFormTarget, normalized, {shouldDirty: true});
            }

            handleCloseAddressForm();
            toast.success(t("document.form.address_create_success", {defaultValue: "Indirizzo salvato"}));
        } catch (error) {
            setAddressFormError(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setAddressSaving(false);
        }
    }, [
        addressFormTarget,
        canManageAddresses,
        companyId,
        connectionId,
        handleCloseAddressForm,
        hasConnectionContext,
        setValue,
        t,
    ]);

    return (
        <>
            <form
                onSubmit={handleSubmit(submit)}
                className={classNames("space-y-3", HEADER_COMPACT_CLASS)}
            >
                <fieldset disabled={!isFormEditable} className={isFormEditable ? "" : "opacity-70"}>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-4 xl:grid-cols-8 max-h-[320px] overflow-x-hidden overflow-y-auto px-0.5">
                    <Controller
                        name="destination_address"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-2 xl:col-span-4">
                                <div className="mb-1 flex items-center justify-between">
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("document.form.destination", {defaultValue: "Destinazione"})}
                                    </label>
                                    {canManageAddresses && (
                                        <button
                                            type="button"
                                            onClick={() => handleOpenAddressForm(name)}
                                            disabled={!hasConnectionContext}
                                            className="text-xs font-medium text-blue-1000 hover:text-blue-900 disabled:cursor-not-allowed disabled:text-gray-400"
                                        >
                                            {t("document.form.add_address_action", {defaultValue: "Nuovo indirizzo"})}
                                        </button>
                                    )}
                                </div>
                                <InputSelect
                                    isShowLabel={false}
                                    label={t("document.form.destination", {defaultValue: "Destinazione"})}
                                    name={name}
                                    options={addressOptions}
                                    value={value}
                                    onChange={(selected) => {
                                        onChange(selected);
                                        setValue(name, selected, {shouldDirty: true});
                                    }}
                                    isLoading={areAddressesLoading && hasConnectionContext && canManageAddresses}
                                    isDisabled={!hasConnectionContext || !canManageAddresses || areAddressesLoading}
                                    errors={error}
                                    isClearable
                                    size="compact"
                                />
                                {!areAddressesLoading && hasConnectionContext && canManageAddresses && addressOptions.length === 0 && (
                                    <p className="mt-2 text-xs text-gray-500">
                                        {t("document.form.no_addresses", {defaultValue: "Nessun indirizzo disponibile"})}
                                    </p>
                                )}
                                {!hasConnectionContext && (
                                    <p className="mt-2 text-xs text-gray-500">
                                        {t("document.form.address_connection_hint", {defaultValue: "Collega prima un cliente per selezionare gli indirizzi"})}
                                    </p>
                                )}
                            </div>
                        )}
                    />

                    <Controller
                        name="delivery_address"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-2 xl:col-span-4">
                                <div className="mb-1 flex items-center justify-between">
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("document.form.delivery_address", {defaultValue: "Indirizzo di consegna"})}
                                    </label>
                                    {canManageAddresses && (
                                        <button
                                            type="button"
                                            onClick={() => handleOpenAddressForm(name)}
                                            disabled={!hasConnectionContext}
                                            className="text-xs font-medium text-blue-1000 hover:text-blue-900 disabled:cursor-not-allowed disabled:text-gray-400"
                                        >
                                            {t("document.form.add_address_action", {defaultValue: "Nuovo indirizzo"})}
                                        </button>
                                    )}
                                </div>
                                <InputSelect
                                    isShowLabel={false}
                                    label={t("document.form.delivery_address", {defaultValue: "Indirizzo di consegna"})}
                                    name={name}
                                    options={addressOptions}
                                    value={value}
                                    onChange={(selected) => {
                                        onChange(selected);
                                        setValue(name, selected, {shouldDirty: true});
                                    }}
                                    isLoading={areAddressesLoading && hasConnectionContext && canManageAddresses}
                                    isDisabled={!hasConnectionContext || !canManageAddresses || areAddressesLoading}
                                    errors={error}
                                    isClearable
                                    size="compact"
                                />
                                {!areAddressesLoading && hasConnectionContext && canManageAddresses && addressOptions.length === 0 && (
                                    <p className="mt-2 text-xs text-gray-500">
                                        {t("document.form.no_addresses", {defaultValue: "Nessun indirizzo disponibile"})}
                                    </p>
                                )}
                                {!hasConnectionContext && (
                                    <p className="mt-2 text-xs text-gray-500">
                                        {t("document.form.address_connection_hint", {defaultValue: "Collega prima un cliente per selezionare gli indirizzi"})}
                                    </p>
                                )}
                            </div>
                        )}
                    />

                    <Controller
                        name="delivery_date"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-2 xl:col-span-4">
                                <InputDate
                                    label={t("document.form.delivery_date", {defaultValue: "Data consegna"})}
                                    name={name}
                                    startDate={value}
                                    onChange={(nextValue) => handleDeliveryDateFieldChange(nextValue, onChange)}
                                    errors={error}
                                    isShowTime={false}
                                />
                            </div>
                        )}
                    />

                </div>
                </fieldset>
            </form>
            {canManageAddresses && (
                <FormPopup
                    title={t("document.form.address_form_title", {defaultValue: "Aggiungi indirizzo"})}
                    isOpen={Boolean(addressFormTarget)}
                    setIsOpen={(open) => {
                        if (!open) {
                            handleCloseAddressForm();
                        }
                    }}
                    defaultWith="max-w-3xl"
                    isActiveCloseBack={!isAddressSaving}
                >
                    <AddressAutocomplete
                        exception={addressFormError}
                        isLoading={isAddressSaving}
                        onSubmitData={handleCreateAddress}
                    />
                </FormPopup>
            )}

            <FormPopup
                title={t("document.form.delivery_date_change_title", {defaultValue: "Aggiorna data consegna"})}
                isOpen={isDeliveryDateConfirmationOpen}
                setIsOpen={(open) => {
                    if (!open) {
                        handleCancelDeliveryDateChange();
                    }
                }}
                defaultWith="max-w-lg"
            >
                <p className="text-sm text-gray-700 mb-4">
                    {t("document.form.delivery_date_change_description", {
                        defaultValue: "Vuoi applicare la nuova data di consegna a tutte le righe del documento?",
                    })}
                </p>
                <Controller
                    name="change_body_delivery_date"
                    control={control}
                    render={({field: {value, onChange}}) => (
                        <InputToggle
                            label={t("document.form.delivery_date_change_toggle_label", {defaultValue: "Applica a tutte le righe"})}
                            description={t("document.form.delivery_date_change_toggle_description", {
                                defaultValue: "Aggiorna automaticamente la data di consegna anche nelle righe del corpo.",
                            })}
                            defaultChecked={Boolean(value)}
                            onChange={onChange}
                            size="compact"
                        />
                    )}
                />
                <div className="mt-6 flex justify-end gap-2">
                    <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        onClick={handleCancelDeliveryDateChange}
                    >
                        {t("app.common.cancel", {defaultValue: "Annulla"})}
                    </button>
                    <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-blue-1000 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        onClick={handleConfirmDeliveryDateChange}
                    >
                        {t("app.common.confirm", {defaultValue: "Conferma"})}
                    </button>
                </div>
            </FormPopup>
        </>
    );
};

DeliveryForm.propTypes = {
    document: PropTypes.object,
    onSubmit: PropTypes.func,
    isSubmitting: PropTypes.bool,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    connectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isEditable: PropTypes.bool,
};

DeliveryForm.defaultProps = {
    document: null,
    onSubmit: null,
    isSubmitting: false,
    companyId: null,
    connectionId: null,
    isEditable: true,
};

export default DeliveryForm;
